import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, IconButton, List, ListItem, ListItemButton, ListItemText, Paper,
  Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useApiClient } from '../../config/axios';

interface UsedByRow {
  pivot_name: string;
  depth: number;
  source_section: string | null;
}
interface MetricRow {
  data_index: string;
  formula_preview: string | null;
  defined_in_n_pivots: number;
}
interface FtlDetail {
  include_path: string;
  byte_size: number;
  content_sha256: string;
  n_metrics: number;
  parse_ok: boolean;
  nested_includes: string[] | null;
  nested_imports: string[] | null;
  body: string;
  loaded_at: string;
  used_by: UsedByRow[];
  metrics: MetricRow[];
}

interface FtlDetailModalProps {
  open: boolean;
  includePath: string | null;
  onClose: () => void;
  // Called when the user clicks a pivot in "Used by" — parent may want
  // to jump-navigate. Optional; if omitted the chips are non-interactive.
  onNavigateToPivot?: (pivotName: string) => void;
  // Called when the user clicks a nested_include chip — same idea, opens
  // that FTL in this same modal.
  onNavigateToFtl?: (includePath: string) => void;
}

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const depthChip = (depth: number): string =>
  depth === 0 ? 'direct' : `via ${depth}`;

const FtlDetailModal: React.FC<FtlDetailModalProps> = ({
  open, includePath, onClose, onNavigateToPivot, onNavigateToFtl,
}) => {
  const apiClient = useApiClient();
  const [detail, setDetail] = useState<FtlDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !includePath) { setDetail(null); return; }
    setLoading(true);
    setError(null);
    apiClient
      .get(`/view-manager/ftls/${encodeURIComponent(includePath)}`)
      .then(r => setDetail(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message || 'Failed to load FTL'))
      .finally(() => setLoading(false));
  }, [apiClient, open, includePath]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '85vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
            {includePath || '…'}
          </Typography>
          {detail && (
            <>
              <Chip size="small" label={formatBytes(detail.byte_size)} />
              <Chip size="small" label={`${detail.n_metrics} metrics`} color="primary" variant="outlined" />
              <Chip size="small" label={`sha:${detail.content_sha256.slice(0, 8)}`} variant="outlined" sx={{ fontFamily: 'monospace' }} />
              {!detail.parse_ok && <Chip size="small" label="parse failed" color="error" />}
            </>
          )}
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : !detail ? null : (
          <>
            {/* Includes/imports strip */}
            {(detail.nested_includes?.length || detail.nested_imports?.length) && (
              <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                {detail.nested_includes?.length ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">nested_includes:</Typography>
                    {detail.nested_includes.map(inc => (
                      <Chip
                        key={inc}
                        size="small"
                        label={inc}
                        variant="outlined"
                        onClick={onNavigateToFtl ? () => onNavigateToFtl(inc) : undefined}
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                    ))}
                  </Stack>
                ) : null}
                {detail.nested_imports?.length ? (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">nested_imports:</Typography>
                    {detail.nested_imports.map(imp => (
                      <Chip
                        key={imp}
                        size="small"
                        label={imp}
                        variant="outlined"
                        onClick={onNavigateToFtl ? () => onNavigateToFtl(imp) : undefined}
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Box>
            )}

            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              {/* Body */}
              <Box sx={{ flex: 2, overflow: 'auto', borderRight: 1, borderColor: 'divider', p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                  Body ({detail.body.split('\n').length} lines)
                </Typography>
                <Paper variant="outlined" sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5 }}>
                  <Box
                    component="pre"
                    sx={{
                      fontFamily: '"Fira Code", Consolas, monospace',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      m: 0,
                      whiteSpace: 'pre',
                      overflow: 'auto',
                    }}
                  >
                    {detail.body}
                  </Box>
                </Paper>
              </Box>

              {/* Right sidebar: used-by + metrics */}
              <Box sx={{ flex: 1, minWidth: 280, overflow: 'auto', p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Used by <Chip size="small" label={detail.used_by.length} sx={{ ml: 0.5, height: 18 }} />
                </Typography>
                {detail.used_by.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    (no pivots reference this FTL)
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ mb: 2 }}>
                    {detail.used_by.map(row => {
                      const rowText = (
                        <ListItemText
                          primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace' } }}
                          primary={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{row.pivot_name}</span>
                              <Chip
                                size="small"
                                label={depthChip(row.depth)}
                                variant={row.depth === 0 ? 'filled' : 'outlined'}
                                sx={{ height: 16, fontSize: '0.65rem' }}
                              />
                            </Stack>
                          }
                        />
                      );
                      const key = `${row.pivot_name}:${row.depth}`;
                      return onNavigateToPivot ? (
                        <ListItemButton
                          key={key}
                          onClick={() => onNavigateToPivot(row.pivot_name)}
                          sx={{ py: 0.25 }}
                        >
                          {rowText}
                        </ListItemButton>
                      ) : (
                        <ListItem key={key} sx={{ py: 0.25 }}>
                          {rowText}
                        </ListItem>
                      );
                    })}
                  </List>
                )}

                <Divider sx={{ mb: 1 }} />

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Metrics defined here <Chip size="small" label={detail.metrics.length} sx={{ ml: 0.5, height: 18 }} />
                </Typography>
                {detail.metrics.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    (no metrics attributed to this FTL — utility include)
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {detail.metrics.map(m => (
                      <ListItem key={m.data_index} sx={{ py: 0.25 }}>
                        <ListItemText
                          primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace' } }}
                          primary={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{m.data_index}</span>
                              <Chip
                                size="small"
                                label={`in ${m.defined_in_n_pivots} pivots`}
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.65rem' }}
                              />
                            </Stack>
                          }
                          secondary={m.formula_preview || undefined}
                          secondaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace', fontSize: '0.65rem' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FtlDetailModal;
